import { ref, computed, watch, onBeforeUnmount } from 'vue';

/**
 * Disclosure/dropdown behaviour: aria-expanded + aria-controls on the trigger,
 * Escape-to-close (returns focus to the trigger), and close-on-outside-click.
 *
 * Two modes:
 * - Uncontrolled (default): the composable owns an internal expanded ref.
 *   `toggle()`/`close()` flip it. Use for self-contained dropdowns.
 * - Controlled: pass `isExpanded` (a Ref/computed the parent owns). The
 *   composable never mutates it; Escape and outside-click call `onClose`, and
 *   the parent is responsible for updating its own state.
 *
 * @param {import('vue').Ref<HTMLElement|null>} containerRef Root the widget lives in (for outside-click).
 * @param {object} [options]
 * @param {import('vue').Ref<boolean>} [options.isExpanded] External state — switches to controlled mode.
 * @param {string} [options.panelId] Id of the revealed panel, exposed via aria-controls.
 * @param {Function} [options.onClose] Called (controlled mode) when Escape/outside-click requests a close.
 */
export function useDisclosure(containerRef, options = {}) {
  const { isExpanded: externalExpanded = null, panelId = undefined, onClose = null } = options;
  const controlled = externalExpanded != null;

  const internalExpanded = ref(false);
  const isExpanded = controlled ? externalExpanded : internalExpanded;

  // The element to return focus to when closing via Escape.
  let triggerEl = null;

  const triggerAttrs = computed(() => ({
    'aria-expanded': String(Boolean(isExpanded.value)),
    ...(panelId ? { 'aria-controls': panelId } : {}),
  }));

  const open = () => {
    if (!controlled) internalExpanded.value = true;
  };

  const close = ({ restoreFocus = false } = {}) => {
    if (controlled) {
      if (isExpanded.value && typeof onClose === 'function') onClose();
    } else {
      internalExpanded.value = false;
    }

    if (restoreFocus && triggerEl instanceof HTMLElement && triggerEl.isConnected) {
      triggerEl.focus();
    }
  };

  const toggle = () => {
    if (isExpanded.value) {
      close();
    } else {
      open();
    }
  };

  const handleKeydown = event => {
    if (event.key === 'Escape') close({ restoreFocus: true });
  };

  const handleOutsideClick = event => {
    if (containerRef.value && !containerRef.value.contains(event.target)) {
      close();
    }
  };

  // Only listen while open. Runs after the reactive update flush, so the click
  // that opened the widget has already finished propagating (no self-close).
  watch(
    () => Boolean(isExpanded.value),
    expanded => {
      if (expanded) {
        if (!triggerEl) triggerEl = document.activeElement;
        document.addEventListener('keydown', handleKeydown);
        document.addEventListener('click', handleOutsideClick);
      } else {
        triggerEl = null;
        document.removeEventListener('keydown', handleKeydown);
        document.removeEventListener('click', handleOutsideClick);
      }
    },
    { immediate: true }
  );

  onBeforeUnmount(() => {
    document.removeEventListener('keydown', handleKeydown);
    document.removeEventListener('click', handleOutsideClick);
  });

  return { isExpanded, open, close, toggle, triggerAttrs };
}
